import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

// Palabras que rotan en el typewriter
const ROLES = [
  'Full Stack Developer',
  'IoT & Robótica Engineer',
  'AI Agent Builder',
  'Soluciones Digitales',
  'Innovador Tecnológico',
];

// Canvas de partículas conectadas (red neuronal visual)
const ParticleCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    let W = (canvas.width = window.innerWidth);
    let H = (canvas.height = window.innerHeight);

    const PARTICLE_COUNT = Math.min(80, Math.floor(W / 18));
    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 1.5 + 0.5,
      alpha: Math.random() * 0.5 + 0.2,
    }));

    const mouse = { x: W / 2, y: H / 2 };
    window.addEventListener('mousemove', (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    });

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      particles.forEach((p) => {
        // Mouse repulsion suave
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 120) {
          p.vx += (dx / dist) * 0.03;
          p.vy += (dy / dist) * 0.03;
        }

        p.x += p.vx;
        p.y += p.vy;

        // Fricción
        p.vx *= 0.99;
        p.vy *= 0.99;

        // Bounce
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;

        // Draw dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,69,0,${p.alpha})`;
        ctx.fill();
      });

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 140) {
            const alpha = (1 - d / 140) * 0.25;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(255,69,0,${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      animId = requestAnimationFrame(draw);
    };

    draw();

    const onResize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        zIndex: 0, pointerEvents: 'none',
      }}
    />
  );
};

// Glitch text effect
const GlitchTitle = ({ text }) => (
  <>
    <style>{`
      .glitch { position: relative; }
      .glitch::before, .glitch::after {
        content: attr(data-text); position: absolute;
        top: 0; left: 0; width: 100%; height: 100%;
      }
      .glitch::before {
        color: #ff4500; animation: glitch-1 3s infinite linear;
        clip-path: polygon(0 0, 100% 0, 100% 35%, 0 35%);
        transform: translateX(-2px);
      }
      .glitch::after {
        color: #ff7b47; animation: glitch-2 3s infinite linear;
        clip-path: polygon(0 65%, 100% 65%, 100% 100%, 0 100%);
        transform: translateX(2px);
      }
      @keyframes glitch-1 {
        0%,90%,100% { opacity:0; transform:translateX(-2px) }
        91%,95% { opacity:1; transform:translateX(-4px) skewX(-2deg) }
        93%,97% { opacity:0; transform:translateX(2px) }
      }
      @keyframes glitch-2 {
        0%,88%,100% { opacity:0; transform:translateX(2px) }
        89%,94% { opacity:1; transform:translateX(4px) skewX(2deg) }
        92%,96% { opacity:0; transform:translateX(-2px) }
      }
      @keyframes scan {
        0% { transform: translateY(-100%); }
        100% { transform: translateY(100vh); }
      }
      .scan-line {
        position:absolute; left:0; width:100%; height:2px;
        background: linear-gradient(transparent, rgba(255,69,0,0.15), transparent);
        animation: scan 4s linear infinite; pointer-events:none; zIndex:1;
      }
      @keyframes blink { 0%,49%{opacity:1} 50%,100%{opacity:0} }
      .cursor { animation: blink 0.8s infinite; color: #ff4500; }
    `}</style>
    <span className="glitch" data-text={text} style={{ display: 'inline-block' }}>
      {text}
    </span>
  </>
);

// Typewriter para los roles
const TypeWriter = ({ words }) => {
  const [idx, setIdx] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const word = words[idx % words.length];
    let timeout;

    if (!deleting && displayed.length < word.length) {
      timeout = setTimeout(() => setDisplayed(word.slice(0, displayed.length + 1)), 80);
    } else if (!deleting && displayed.length === word.length) {
      timeout = setTimeout(() => setDeleting(true), 2000);
    } else if (deleting && displayed.length > 0) {
      timeout = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 40);
    } else if (deleting && displayed.length === 0) {
      setDeleting(false);
      setIdx((i) => i + 1);
    }

    return () => clearTimeout(timeout);
  }, [displayed, deleting, idx, words]);

  return (
    <span>
      <span style={{ color: '#ff4500', fontWeight: 600 }}>{displayed}</span>
      <span className="cursor">|</span>
    </span>
  );
};

// Tech floating badges
const TECH_BADGES = [
  { label: 'Node.js', x: '8%', y: '20%', delay: 0 },
  { label: 'React', x: '85%', y: '15%', delay: 0.5 },
  { label: 'Groq AI', x: '78%', y: '70%', delay: 1 },
  { label: 'IoT', x: '12%', y: '72%', delay: 1.5 },
  { label: 'MongoDB', x: '90%', y: '42%', delay: 0.8 },
  { label: 'Docker', x: '5%', y: '46%', delay: 1.2 },
];

const FloatingBadge = ({ label, x, y, delay }) => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  if (isMobile) return null;
  return (
    <motion.div
      style={{
        position: 'absolute', left: x, top: y,
        padding: '0.3rem 0.8rem', borderRadius: '20px',
        background: 'rgba(255,69,0,0.08)',
        border: '1px solid rgba(255,69,0,0.25)',
        color: 'rgba(242,242,242,0.6)', fontSize: '0.75rem',
        backdropFilter: 'blur(10px)', zIndex: 2,
      }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{
        opacity: [0, 0.8, 0.8],
        scale: [0.8, 1, 1],
        y: [0, -8, 0],
      }}
      transition={{
        delay,
        duration: 4,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut',
      }}
    >
      {label}
    </motion.div>
  );
};

const Hero = () => {
  const handleScroll = (e, id) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="index"
      style={{
        position: 'relative', minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#0f0f0f', overflow: 'hidden',
      }}
    >
      <ParticleCanvas />
      <div className="scan-line" />

      {/* Floating tech badges (desktop only) */}
      {TECH_BADGES.map((b) => <FloatingBadge key={b.label} {...b} />)}

      {/* Glow orb */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '600px', height: '600px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,69,0,0.06) 0%, transparent 70%)',
        pointerEvents: 'none', zIndex: 1,
      }} />

      {/* Main content */}
      <motion.div
        style={{
          position: 'relative', zIndex: 3, textAlign: 'center',
          padding: '0 1.5rem', maxWidth: '860px',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.4rem 1rem', borderRadius: '20px',
            background: 'rgba(255,69,0,0.1)', border: '1px solid rgba(255,69,0,0.3)',
            color: '#ff7b47', fontSize: '0.8rem', marginBottom: '1.5rem',
            letterSpacing: '2px', textTransform: 'uppercase',
          }}
        >
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#44ff88', animation: 'pulse-dot 2s infinite' }} />
          Disponible para proyectos
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          style={{
            fontSize: 'clamp(2.5rem, 7vw, 5rem)',
            fontWeight: 800, color: '#f2f2f2',
            lineHeight: 1.1, marginBottom: '0.5rem',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          <GlitchTitle text="TinchoDev" />
        </motion.h1>

        {/* Subtitle typewriter */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          style={{
            fontSize: 'clamp(1rem, 2.5vw, 1.4rem)',
            color: 'rgba(242,242,242,0.7)', marginBottom: '0.75rem',
            minHeight: '2rem',
          }}
        >
          <TypeWriter words={ROLES} />
        </motion.p>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          style={{
            color: 'rgba(242,242,242,0.45)', fontSize: '0.95rem',
            maxWidth: '580px', margin: '0 auto 2.5rem',
            lineHeight: 1.7,
          }}
        >
          Construyo soluciones digitales que fusionan hardware, software e inteligencia artificial.
          Del mundo físico al digital — sin límites.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}
        >
          <motion.a
            href="#contact"
            onClick={(e) => handleScroll(e, 'contact')}
            whileHover={{ scale: 1.04, boxShadow: '0 0 30px rgba(255,69,0,0.6)' }}
            whileTap={{ scale: 0.97 }}
            style={{
              display: 'inline-block', padding: '0.85rem 2.2rem',
              background: 'linear-gradient(135deg, #ff4500, #ff7b47)',
              color: 'white', borderRadius: '8px', textDecoration: 'none',
              fontWeight: 700, fontSize: '0.95rem',
              boxShadow: '0 0 20px rgba(255,69,0,0.35)',
              letterSpacing: '0.5px',
            }}
          >
            Trabajemos juntos →
          </motion.a>
          <motion.a
            href="#projects"
            onClick={(e) => handleScroll(e, 'projects')}
            whileHover={{ scale: 1.04, background: 'rgba(255,69,0,0.15)' }}
            whileTap={{ scale: 0.97 }}
            style={{
              display: 'inline-block', padding: '0.85rem 2.2rem',
              background: 'transparent',
              border: '1px solid rgba(255,69,0,0.4)',
              color: '#ff7b47', borderRadius: '8px', textDecoration: 'none',
              fontWeight: 600, fontSize: '0.95rem',
              transition: 'all 0.2s',
            }}
          >
            Ver proyectos
          </motion.a>
          <motion.a
            href="https://drive.google.com/file/d/1_your_cv_id/view"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.85rem 1.5rem',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(242,242,242,0.6)', borderRadius: '8px', textDecoration: 'none',
              fontWeight: 500, fontSize: '0.88rem',
              transition: 'all 0.2s',
            }}
          >
            <span>📄</span> Descargar CV
          </motion.a>
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          style={{
            display: 'flex', gap: '2rem', justifyContent: 'center',
            marginTop: '3.5rem', flexWrap: 'wrap',
          }}
        >
          {[
            { n: '3+', label: 'Años de experiencia' },
            { n: '15+', label: 'Proyectos entregados' },
            { n: '2', label: 'Especialidades' },
            { n: '∞', label: 'Ganas de innovar' },
          ].map((s) => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#ff4500' }}>{s.n}</div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(242,242,242,0.4)', textTransform: 'uppercase', letterSpacing: '1px' }}>{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        style={{
          position: 'absolute', bottom: '2rem', left: '50%',
          transform: 'translateX(-50%)', zIndex: 3,
        }}
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div style={{
          width: '24px', height: '40px', border: '2px solid rgba(255,69,0,0.4)',
          borderRadius: '12px', display: 'flex', justifyContent: 'center', paddingTop: '6px',
        }}>
          <motion.div
            style={{ width: '4px', height: '8px', background: '#ff4500', borderRadius: '2px' }}
            animate={{ y: [0, 10, 0], opacity: [1, 0, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
